import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { CalendarIcon } from "lucide-react";
import { Bar, BarChart, Cell, Pie, PieChart, XAxis, YAxis } from "recharts";
import type { ChartConfig } from "@/components/ui/chart";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export const Route = createFileRoute("/_app/analytics")({
  component: RouteComponent,
});

const piiDistributionData = [
  { name: "SSN", value: 35, fill: "#C11574" },
  { name: "Credit Card", value: 25, fill: "#FCCEEE" },
  { name: "Phone Number", value: 15, fill: "#DD2590" },
  { name: "Company Name", value: 10, fill: "#EE46BC" },
  { name: "Address", value: 8, fill: "#F670C7" },
  { name: "Person Name", value: 7, fill: "#FAA7E0" },
];

const weeklyRiskData = [
  { day: "Mon", lowRisk: 300, mediumRisk: 200, highRisk: 100 },
  { day: "Tue", lowRisk: 400, mediumRisk: 350, highRisk: 200 },
  { day: "Wed", lowRisk: 350, mediumRisk: 300, highRisk: 150 },
  { day: "Thu", lowRisk: 400, mediumRisk: 350, highRisk: 200 },
  { day: "Fri", lowRisk: 450, mediumRisk: 350, highRisk: 150 },
  { day: "Sat", lowRisk: 400, mediumRisk: 350, highRisk: 200 },
  { day: "Sun", lowRisk: 350, mediumRisk: 300, highRisk: 150 },
];

const piiChartConfig = {
  SSN: { label: "SSN", color: "#C11574" },
  "Credit Card": { label: "Credit Card", color: "#FCCEEE" },
  "Phone Number": { label: "Phone Number", color: "#DD2590" },
  "Company Name": { label: "Company Name", color: "#EE46BC" },
  Address: { label: "Address", color: "#F670C7" },
  "Person Name": { label: "Person Name", color: "#FAA7E0" },
} satisfies ChartConfig;

const riskChartConfig = {
  lowRisk: { label: "Low Risk", color: "#EE46BC" },
  mediumRisk: { label: "Medium Risk", color: "#FAA7E0" },
  highRisk: { label: "High Risk", color: "#C11574" },
} satisfies ChartConfig;

const departmentRiskData = [
  {
    department: "Sales",
    meetings: 124,
    piiCount: 93,
    avgRiskScore: 94,
    status: "Critical" as const,
  },
  {
    department: "Engineering",
    meetings: 371,
    piiCount: 180,
    avgRiskScore: 21,
    status: "Processing" as const,
  },
  {
    department: "HR",
    meetings: 14,
    piiCount: 2,
    avgRiskScore: 99,
    status: "Completed" as const,
  },
  {
    department: "Finance",
    meetings: 48,
    piiCount: 11,
    avgRiskScore: 77,
    status: "Processing" as const,
  },
  {
    department: "Marketing",
    meetings: 60,
    piiCount: 9,
    avgRiskScore: 7,
    status: "Live" as const,
  },
];

type DepartmentStatus = "Critical" | "Processing" | "Completed" | "Live";

function StatusBadge({ status }: { status: DepartmentStatus }) {
  const statusStyles = {
    Critical:
      "bg-red-50 text-red-600 border-red-200 dark:bg-red-950/30 dark:text-red-400 dark:border-red-800",
    Processing:
      "bg-pink-50 text-pink-600 border-pink-200 dark:bg-pink-950/30 dark:text-pink-400 dark:border-pink-800",
    Completed:
      "bg-green-50 text-green-600 border-green-200 dark:bg-green-950/30 dark:text-green-400 dark:border-green-800",
    Live: "bg-green-50 text-green-600 border-green-200 dark:bg-green-950/30 dark:text-green-400 dark:border-green-800",
  };

  return (
    <span
      className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium ${statusStyles[status]}`}
    >
      {status}
    </span>
  );
}

function RiskScoreBar({ score }: { score: number }) {
  return (
    <div className="flex items-center gap-3">
      <div className="h-2 w-24 overflow-hidden rounded-full bg-gray-200">
        <div
          className="h-full rounded-full bg-pink-500"
          style={{ width: `${score}%` }}
        />
      </div>
      <span className="text-sm text-muted-foreground">
        {Math.round(score).toString().padStart(2, "0")}
      </span>
    </div>
  );
}

function DepartmentRiskHeatmap() {
  return (
    <Card>
      <CardHeader className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
        <CardTitle>Department Risk Heatmap</CardTitle>
        <button className="text-sm font-medium text-pink-600 hover:text-pink-700">
          Export Report
        </button>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Department</TableHead>
                <TableHead>Meetings</TableHead>
                <TableHead>PII Count</TableHead>
                <TableHead>Avg Risk Score</TableHead>
                <TableHead className="text-right">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {departmentRiskData.map((row) => (
                <TableRow key={row.department}>
                  <TableCell className="font-medium">
                    {row.department}
                  </TableCell>
                  <TableCell>{row.meetings}</TableCell>
                  <TableCell>{row.piiCount}</TableCell>
                  <TableCell>
                    <RiskScoreBar score={row.avgRiskScore} />
                  </TableCell>
                  <TableCell className="text-right">
                    <StatusBadge status={row.status} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

function PiiLegend() {
  const legendItems = [
    { name: "SSN", color: "#C11574" },
    { name: "Credit Card", color: "#FCCEEE" },
    { name: "Phone Number", color: "#DD2590" },
    { name: "Company Name", color: "#EE46BC" },
    { name: "Address", color: "#F670C7" },
    { name: "Person Name", color: "#FAA7E0" },
  ];

  return (
    <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 pt-4">
      {legendItems.map((item) => (
        <div key={item.name} className="flex items-center gap-1.5">
          <div
            className="size-2.5 shrink-0 rounded-full"
            style={{ backgroundColor: item.color }}
          />
          <span className="text-sm text-muted-foreground">{item.name}</span>
        </div>
      ))}
    </div>
  );
}

interface MetricCardProps {
  title: string;
  value: string;
  subtitle: string;
  subtitleColor?: "green" | "red" | "default";
}

function MetricCard({
  title,
  value,
  subtitle,
  subtitleColor = "default",
}: MetricCardProps) {
  const subtitleColorClass =
    subtitleColor === "green"
      ? "text-green-600"
      : subtitleColor === "red"
        ? "text-red-600"
        : "text-muted-foreground";

  return (
    <Card className="py-4">
      <CardContent className="space-y-1">
        <p className="text-sm text-muted-foreground">{title}</p>
        <p className="text-4xl font-bold tracking-tight">{value}</p>
        <p className={`text-xs ${subtitleColorClass}`}>{subtitle}</p>
      </CardContent>
    </Card>
  );
}

function RouteComponent() {
  const [timeFilter, setTimeFilter] = useState("all-time");

  return (
    <div className="space-y-6 p-6">
      <h1 className="mb-6 text-3xl font-bold">Analytics</h1>
      <div className="flex justify-end">
        <Select value={timeFilter} onValueChange={setTimeFilter}>
          <SelectTrigger className="w-36">
            <CalendarIcon className="mr-2 size-4" />
            <SelectValue placeholder="Select time" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all-time">All Time</SelectItem>
            <SelectItem value="this-week">This Week</SelectItem>
            <SelectItem value="this-month">This Month</SelectItem>
            <SelectItem value="last-30-days">Last 30 Days</SelectItem>
            <SelectItem value="last-90-days">Last 90 Days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title="Compliance Score"
          value="98%"
          subtitle="+3% this week"
          subtitleColor="green"
        />
        <MetricCard
          title="Exposure Risk"
          value="13%"
          subtitle="-12% last month"
          subtitleColor="green"
        />
        <MetricCard
          title="Critical Alerts"
          value="5"
          subtitle="Requires attention"
          subtitleColor="red"
        />
        <MetricCard
          title="Total Meetings"
          value="174"
          subtitle="+18% from last week"
          subtitleColor="green"
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>PII Type Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer
              config={piiChartConfig}
              className="mx-auto aspect-square max-h-[350px]"
            >
              <PieChart>
                <ChartTooltip
                  content={<ChartTooltipContent nameKey="name" hideLabel />}
                />
                <Pie
                  data={piiDistributionData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={120}
                  strokeWidth={2}
                  stroke="white"
                >
                  {piiDistributionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
              </PieChart>
            </ChartContainer>
            <PiiLegend />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Weekly Risk Trends</CardTitle>
          </CardHeader>
          <CardContent>
            <ChartContainer
              config={riskChartConfig}
              className="h-[350px] w-full"
            >
              <BarChart data={weeklyRiskData}>
                <XAxis
                  dataKey="day"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={10}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tickMargin={10}
                  tickFormatter={(value) => value.toLocaleString()}
                />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Bar
                  dataKey="lowRisk"
                  stackId="risk"
                  fill="#EE46BC"
                  radius={[0, 0, 0, 0]}
                />
                <Bar
                  dataKey="mediumRisk"
                  stackId="risk"
                  fill="#FAA7E0"
                  radius={[0, 0, 0, 0]}
                />
                <Bar
                  dataKey="highRisk"
                  stackId="risk"
                  fill="#C11574"
                  radius={[4, 4, 0, 0]}
                />
                <ChartLegend content={<ChartLegendContent />} />
              </BarChart>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>
      <DepartmentRiskHeatmap />
    </div>
  );
}
